import { useStore, useStoreMap } from 'effector-react'
import { $carsCount, $fetchingCarsCount } from 'features/routing-panel/model'
import { PreparedCard } from 'features/routing-panel/types'
import { compileStyles } from 'lib/styled-helpers'
import React, { FC, useEffect } from 'react'
import styled from 'styled-components'
import { Icon } from 'ui/icon'

import { getProjectName } from '../../../lib/get-project-name'
import { Loader } from '../../../ui/loader'
import { mergeValues, removeValues } from '../../filter/model'
import { $filterValues } from '../../filter/model'
import { BaseCard } from './BaseCard'

export type Props = PreparedCard & {
  onActiveChange(value: boolean): void
  showCarsCount: boolean
}

export const CardFilter: FC<Props> = ({ filter, image, price, name, onActiveChange, showCarsCount, id, imageId }) => {
  const carsCount = useStoreMap({
    store: $carsCount,
    keys: [id],
    fn: (store, [id]) => (store ? store.find((card) => card.id === id)?.count : null)
  })

  const isActive = useStoreMap({
    store: $filterValues,
    keys: [filter],
    fn: (filter, [cardFilter]) => {
      if (filter && cardFilter) {
        return Object.keys(cardFilter).every((cardFilterKey) => {
          const cardFilterValue = cardFilter[cardFilterKey]
          const filterValue = filter[cardFilterKey]
          return filterValue && cardFilterValue.every((value) => filterValue.includes(value))
        })
      } else {
        return false
      }
    }
  })

  useEffect(() => {
    onActiveChange(isActive)
  }, [isActive, onActiveChange])

  const fetchingCarsCount = useStore($fetchingCarsCount)

  const handleCheckboxChange = () => {
    isActive ? removeValues(filter) : mergeValues(filter)
  }

  return (
    <Label>
      <BaseCard
        image={image}
        name={name}
        price={price}
        id={id}
        imageId={imageId}
        showCarsCount={showCarsCount}
        fetchingCarsCount={fetchingCarsCount}
        carsCount={carsCount}
        isActive={isActive}
      >
        {showCarsCount && getProjectName() !== 'generic_v2' && (
          <CarsCount isActive={isActive}>{fetchingCarsCount ? <Loader /> : carsCount}</CarsCount>
        )}

        <Input type="checkbox" onChange={handleCheckboxChange} checked={isActive} />
        {isActive && getProjectName() !== 'generic_v2' && (
          <ActiveIcon position={showCarsCount ? 'right-bottom' : 'left-top'}>
            <Icon name="check" size={0.65} />
          </ActiveIcon>
        )}
      </BaseCard>
    </Label>
  )
}

const SkeletonWrapper = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing[1]};
  left: ${({ theme }) => theme.spacing[1]};
  width: ${({ theme }) => theme.spacing[12]};
  height: ${({ theme }) => theme.spacing[12]};
`

const CarsCount = styled.div<{ isActive: boolean }>`
  top: ${({ theme }) => theme.spacing[1]};
  left: ${({ theme }) => theme.spacing[1]};
  margin: 0;
  color: ${({ theme, isActive }) => (isActive ? theme.palette.black : theme.palette.grey[100])};
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme, isActive }) =>
    getProjectName() === 'tank' && isActive ? theme.palette.brand.primary : theme.palette.grey[100]};
  transition: color 0.25s cubic-bezier(0.445, 0.05, 0.55, 0.95);
  ${({ theme }) =>
    `${getProjectName() === 'mini' ? compileStyles(theme.textStyles.h3) : compileStyles(theme.textStyles.h1)}`};
`

const Label = styled.label`
  &:hover ${CarsCount} {
    color: ${({ theme }) => (getProjectName() === 'tank' ? theme.palette.brand.primary : theme.palette.black)};
  }
`

const Input = styled.input`
  display: none;
`

const ActiveIcon = styled.div<{ position: 'right-bottom' | 'left-top' }>`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ position, theme }) => {
    if (position === 'left-top') {
      return {
        left: theme.spacing[4],
        top: theme.spacing[1]
      }
    } else {
      return {
        right: theme.spacing[2],
        bottom: `-${theme.spacing[4]}`
      }
    }
  }};

  width: ${({ theme }) => theme.spacing[6]};
  height: ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.palette.brand.primary};
  color: ${({ theme }) => theme.palette.white};
  border-radius: 50%;
`
